// /app/frontend/src/pages/portal/PortalLoginPage.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePortal } from '../../contexts/PortalContext';
import { useRBAC } from '../../contexts/RBACContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Shield, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function PortalLoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { refreshModules } = useRBAC();
  const { clientInfo, getBrandingColor, getLogo, isLoading: portalLoading, error: portalError } = usePortal();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await login(email, password);
      refreshModules();
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading while fetching portal info
  if (portalLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800">
        <Loader2 className="h-8 w-8 animate-spin text-white" />
      </div>
    );
  }

  // Show error if portal not found
  if (portalError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-red-500">Portal Not Found</CardTitle>
            <CardDescription>
              The client portal you're trying to access doesn't exist or is not configured.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const primaryColor = getBrandingColor('primary');
  const logo = getLogo();

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background: `linear-gradient(135deg, ${primaryColor}20 0%, #1e293b 50%, #0f172a 100%)`
      }}
    >
      <Card className="w-full max-w-md shadow-2xl border-0 bg-card/95 backdrop-blur">
        <CardHeader className="space-y-4 text-center pb-2">
          {/* Logo or Client Name */}
          {logo ? (
            <img 
              src={logo} 
              alt={clientInfo?.client_name} 
              className="h-16 mx-auto object-contain"
            />
          ) : (
            <div className="flex justify-center">
              <div 
                className="p-3 rounded-xl"
                style={{ backgroundColor: `${primaryColor}20` }}
              >
                <Shield className="h-10 w-10" style={{ color: primaryColor }} />
              </div>
            </div>
          )}
          
          <div>
            <CardTitle className="text-2xl font-bold">
              {clientInfo?.client_name || 'Client Portal'}
            </CardTitle>
            <CardDescription className="text-muted-foreground mt-1">
              Sign in to access your compliance portal
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent className="pt-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-11"
                data-testid="portal-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-11"
                data-testid="portal-password-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-11 text-base font-medium"
              style={{ backgroundColor: primaryColor }}
              disabled={isLoading}
              data-testid="portal-login-button"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-xs text-muted-foreground">
              Powered by DataShield Portal
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
